/**
 * User detail → Activity: what this student actually did, day by day.
 *
 * Reuses the platform's Engagement-by-Activities chart, but plotting EVENT
 * counts rather than distinct users — for a single user "distinct users per
 * day" is only ever 0 or 1, which draws a meaningless square wave.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { userApi } from '../../../api/userApi';
import { EngagementByActivitiesChart } from '../../dashboard/EngagementByActivitiesChart';
import './ActivityTab.css';

const RANGES = [
    { days: 30, label: '30 days' },
    { days: 60, label: '60 days' },
    { days: 90, label: '90 days' },
] as const;

/** Beyond this many days the daily line is too dense to read; bucket by week. */
const WEEKLY_ABOVE_DAYS = 45;

export function ActivityTab({ userId }: { userId: string }) {
    const [days, setDays] = useState<number>(30);

    const { data, isLoading, error } = useQuery({
        queryKey: ['user-activity', userId, days],
        queryFn: () => userApi.getUserActivity(userId, days),
        staleTime: 60_000,
    });

    return (
        <div className="user-activity-tab">
            <div className="user-activity-controls">
                {RANGES.map(r => (
                    <button
                        key={r.days}
                        type="button"
                        className={`user-activity-range ${days === r.days ? 'is-active' : ''}`}
                        onClick={() => setDays(r.days)}
                    >
                        {r.label}
                    </button>
                ))}
                {data && (
                    <span className="user-activity-summary">
                        {data.totalEvents} actions across {data.activeDays} active day{data.activeDays === 1 ? '' : 's'}
                    </span>
                )}
            </div>

            {isLoading ? <p className="user-activity-empty">Loading…</p>
                : error ? <p className="user-activity-empty">Couldn't load activity.</p>
                : !data ? null
                : data.totalEvents === 0 ? <p className="user-activity-empty">No recorded activity in this range.</p>
                : (
                    <EngagementByActivitiesChart
                        series={data.series}
                        weekly={days > WEEKLY_ABOVE_DAYS}
                        metric="events"
                        title="Activity over time"
                    />
                )}
        </div>
    );
}
